import { lazy, Suspense, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ROUTE_SLUGS } from "@/content/routes";
import { useLocale } from "@/hooks/useLocale";
import { RouteErrorBoundary } from "@/components/shared/RouteErrorBoundary";

const HomePage = lazy(() => import("@/pages/index"));
const DayPage = lazy(() => import("@/pages/day"));
const PlatformPage = lazy(() => import("@/pages/platform"));
const BusinessSoftwarePage = lazy(() => import("@/pages/business-software"));
const PlatformToolPage = lazy(() => import("@/pages/platform/tool"));
const PricingPage = lazy(() => import("@/pages/pricing"));
const SecurityPage = lazy(() => import("@/pages/security"));
const UseCasesIndexPage = lazy(() => import("@/pages/use-cases/index"));
const UseCaseSectorPage = lazy(() => import("@/pages/use-cases/sector"));
const CaseStudiesPage = lazy(() => import("@/pages/case-studies/index"));
const CaseStudyPage = lazy(() => import("@/pages/case-studies/story"));
const AboutPage = lazy(() => import("@/pages/about"));
const ContactPage = lazy(() => import("@/pages/contact"));
const IntegrationsIndexPage = lazy(() => import("@/pages/integrations/index"));
const BlogIndexPage = lazy(() => import("@/pages/blog/index"));
const BlogPostPage = lazy(() => import("@/pages/blog/post"));
const PrivacyPage = lazy(() => import("@/pages/legal/privacy"));
const TermsPage = lazy(() => import("@/pages/legal/terms"));
const CookiesPage = lazy(() => import("@/pages/legal/cookies"));
const AccessibilityPage = lazy(() => import("@/pages/legal/accessibility"));
const DemoPage = lazy(() => import("@/pages/demo"));
const LoginPage = lazy(() => import("@/pages/login"));
const SignupPage = lazy(() => import("@/pages/signup"));
const RicLandingPage = lazy(() => import("@/pages/ric"));
const NotFoundPage = lazy(() => import("@/pages/404"));

function LocaleSync() {
  const { i18n } = useTranslation();
  const locale = useLocale();
  useEffect(() => {
    if (i18n.language !== locale) void i18n.changeLanguage(locale);
  }, [i18n, locale]);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const Loading = () => (
  <div className="grid min-h-screen place-items-center text-rc-gray">…</div>
);

export function AppRouter() {
  return (
    <RouteErrorBoundary>
      <LocaleSync />
      <ScrollToTop />
      <Suspense fallback={<Loading />}>
        <Routes>
          {(["fr", "en"] as const).map((locale) => {
            const prefix = locale === "en" ? "/en" : "";
            const slug = (key: keyof typeof ROUTE_SLUGS) =>
              ROUTE_SLUGS[key][locale];
            return (
              <Route key={locale}>
                <Route path={`${prefix || "/"}`} element={<HomePage />} />
                <Route
                  path={`${prefix}/${slug("day")}`}
                  element={<DayPage />}
                />
                <Route
                  path={`${prefix}/${slug("businessSoftware")}`}
                  element={<BusinessSoftwarePage />}
                />
                <Route
                  path={`${prefix}/${slug("platform")}`}
                  element={<PlatformPage />}
                />
                <Route
                  path={`${prefix}/${slug("platform")}/:tool`}
                  element={<PlatformToolPage />}
                />
                <Route
                  path={`${prefix}/${slug("pricing")}`}
                  element={<PricingPage />}
                />
                <Route
                  path={`${prefix}/${slug("security")}`}
                  element={<SecurityPage />}
                />
                <Route
                  path={`${prefix}/${slug("useCases")}`}
                  element={<UseCasesIndexPage />}
                />
                <Route path={`${prefix}/${slug("caseStudies")}`} element={<CaseStudiesPage />} />
                <Route path={`${prefix}/${slug("caseStudies")}/:storySlug`} element={<CaseStudyPage />} />
                <Route
                  path={`${prefix}/${slug("useCases")}/:sector`}
                  element={<UseCaseSectorPage />}
                />
                <Route
                  path={`${prefix}/${slug("legalPrivacy")}`}
                  element={<PrivacyPage />}
                />
                <Route
                  path={`${prefix}/${slug("legalTerms")}`}
                  element={<TermsPage />}
                />
                <Route
                  path={`${prefix}/${slug("legalCookies")}`}
                  element={<CookiesPage />}
                />
                <Route
                  path={`${prefix}/${slug("legalAccessibility")}`}
                  element={<AccessibilityPage />}
                />
                <Route
                  path={`${prefix}/${slug("contact")}`}
                  element={<ContactPage />}
                />
                <Route
                  path={`${prefix}/${slug("about")}`}
                  element={<AboutPage />}
                />
                <Route
                  path={`${prefix}/${slug("integrations")}`}
                  element={<IntegrationsIndexPage />}
                />
                <Route
                  path={`${prefix}/${slug("blog")}`}
                  element={<BlogIndexPage />}
                />
                <Route
                  path={`${prefix}/${slug("blog")}/:postSlug`}
                  element={<BlogPostPage />}
                />
                <Route
                  path={`${prefix}/${slug("demo")}`}
                  element={<DemoPage />}
                />
                <Route
                  path={`${prefix}/${slug("login")}`}
                  element={<LoginPage />}
                />
                <Route
                  path={`${prefix}/${slug("signup")}`}
                  element={<SignupPage />}
                />
                <Route
                  path={`${prefix}/${slug("ric")}`}
                  element={<RicLandingPage />}
                />
              </Route>
            );
          })}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </RouteErrorBoundary>
  );
}
